import { BASE_PATH, ROUTES } from '@shared/constants';
import { logger } from './logger';

// ============================================
// Constants
// ============================================

const SESSION_STORAGE_KEY = 'mmt_session_id';
const JWT_TOKEN_STORAGE_KEY = 'mmt_jwt_token';

// ============================================
// SessionStorage helpers
// ============================================

export function getSessionIdFromStorage(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setSessionIdInStorage(sessionId: string): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    logger.warn('[Session] Failed to write sessionId to sessionStorage');
  }
}

/**
 * Clear the stored session ID and JWT token from sessionStorage.
 * Call on logout or when a session error requires re-initialization.
 */
export function clearSessionId(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(JWT_TOKEN_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

// ============================================
// JWT Token Storage
// ============================================

export function getJwtToken(): string | null {
  try {
    return sessionStorage.getItem(JWT_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setJwtToken(token: string): void {
  try {
    sessionStorage.setItem(JWT_TOKEN_STORAGE_KEY, token);
  } catch {
    logger.warn('[Session] Failed to write JWT token to sessionStorage');
  }
}

export function clearJwtToken(): void {
  try {
    sessionStorage.removeItem(JWT_TOKEN_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

// ============================================
// URL helpers
// ============================================

/**
 * Extracts session ID from URL path or query parameters.
 * Supports:
 * - /myra/chat/:sessionId (path-based)
 * - /myra/chat/thankyou?sessionId=xxx (query param)
 */
export function getSessionIdFromUrl(): string | null {
  const path = window.location.pathname;

  const escapedBasePath = BASE_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const chatMatch = path.match(new RegExp(`^${escapedBasePath}/chat/([a-z0-9-]+)$`, 'i'));
  if (chatMatch?.[1]) {
    return chatMatch[1];
  }

  const searchParams = new URLSearchParams(window.location.search);
  const querySessionId = searchParams.get('sessionId');
  if (querySessionId) {
    return querySessionId;
  }

  return null;
}

/**
 * Updates the URL to include the session ID via pushState.
 */
export function createSessionInUrl(sessionId: string): void {
  const newPath = `${ROUTES.CHAT}/${sessionId}`;
  window.history.pushState({}, '', newPath);
}

// ============================================
// Primary session getter
// ============================================

/**
 * Gets the current initialized session ID.
 *
 * Returns from sessionStorage (source of truth for initialized sessions).
 * Falls back to URL for backward compatibility, but never creates a new
 * session — new sessions must always go through initSessionThunk or
 * createSessionThunk so they are registered with the backend.
 */
export function getOrCreateSessionId(): string {
  return getSessionIdFromStorage() ?? getSessionIdFromUrl() ?? '';
}

// ============================================
// Conversation request ID (crId)
// ============================================

/**
 * Generates a conversation request ID (crId) for chat stream API.
 * Format: cr-{random alphanumeric string}
 */
export function generateCrId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'cr-';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function getOrCreateCrId(): string {
  return generateCrId();
}

// ============================================
// PAT Bypass
// ============================================

export const VALID_PAT_VALUE = '334_91_p94_40ken';

export function getPat(): string | null {
  const searchParams = new URLSearchParams(window.location.search);
  const patFromUrl = searchParams.get('pat');

  if (patFromUrl === VALID_PAT_VALUE) {
    return patFromUrl;
  }

  return null;
}

export function isPatBypassActive(): boolean {
  return getPat() !== null;
}
